import { type Locale } from '@aysar/contracts';
import { useEffect, useMemo, useState } from 'react';

import { useBrand } from '@/shared/hooks/useBrand/useBrand';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { numberLocale } from '@/shared/utils/format-number/format-number.util';
import { NO_INSTANT, formatDateTime } from '@/shared/utils/format-datetime/format-datetime.util';

/**
 * How often a rendered age is recomputed.
 *
 * A minute, because that is the coarsest unit whose *first* step the operator can
 * see: "just now" becoming "1 minute ago" is the only transition fast enough to
 * matter, and everything above it changes far more slowly than a tick.
 */
export const RELATIVE_TICK_MS = 60_000;

export interface RelativeTimeView {
  /** The machine-readable instant for `<time dateTime>`, or null if there is none. */
  iso: string | null;
  /** "3 minutes ago" — the answer the operator reads. */
  relative: string;
  /** The full date and time, for the tooltip and the screen-reader text. */
  absolute: string;
}

interface LadderStep {
  /** The step applies to any difference smaller than this. */
  limit: number;
  ms: number;
  unit: Intl.RelativeTimeFormatUnit;
}

/** The coarsest step, and the one anything larger falls back to. */
const YEARS: LadderStep = { limit: Number.POSITIVE_INFINITY, ms: 365 * 86_400_000, unit: 'year' };

/** The ladder, coarsest step last. */
const LADDER: readonly LadderStep[] = [
  { limit: 45_000, ms: 1_000, unit: 'second' },
  { limit: 45 * 60_000, ms: 60_000, unit: 'minute' },
  { limit: 22 * 3_600_000, ms: 3_600_000, unit: 'hour' },
  { limit: 6 * 86_400_000, ms: 86_400_000, unit: 'day' },
  { limit: 26 * 86_400_000, ms: 7 * 86_400_000, unit: 'week' },
  { limit: 320 * 86_400_000, ms: 30 * 86_400_000, unit: 'month' },
  YEARS,
];

const cache = new Map<string, Intl.RelativeTimeFormat>();

function relativeFormatter(locale: Locale): Intl.RelativeTimeFormat {
  const tag = numberLocale(locale);
  const cached = cache.get(tag);
  if (cached !== undefined) return cached;

  // `numeric: 'auto'` is what turns "1 day ago" into "yesterday" and a zero
  // difference into "now" — the words an operator would actually use.
  const format = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });
  cache.set(tag, format);
  return format;
}

function relativeOf(instant: number, now: number, locale: Locale): string {
  const deltaMs = instant - now;
  const step = LADDER.find((candidate) => Math.abs(deltaMs) < candidate.limit) ?? YEARS;

  // Rounded, not truncated: 59 minutes is much better read as "1 hour ago" than
  // as "0 hours ago", which is what a truncating divide produces at every
  // boundary of the ladder.
  return relativeFormatter(locale).format(Math.round(deltaMs / step.ms), step.unit);
}

/**
 * An instant, in words, kept current while the operator is looking at it.
 *
 * Two behaviours are the point of the hook rather than of a formatter:
 *
 * - it **ticks**, because a panel left open on a second monitor would otherwise
 *   say "2 minutes ago" for an hour, and an age that silently stops being true
 *   is worse than a raw timestamp;
 * - it **stops ticking while the tab is hidden**, and catches up immediately
 *   when it comes back. A hidden tab re-rendering every minute forever costs
 *   real battery for something nobody is reading.
 *
 * It always returns the absolute form too. A tooltip is never the only place a
 * fact lives (it is unreachable by touch and by a screen reader), so the caller
 * has both without reaching for a second formatter.
 */
export function useRelativeTime(value: string | null | undefined): RelativeTimeView {
  const { locale } = useDirection();
  const { formats } = useBrand();
  const [now, setNow] = useState(() => Date.now());

  const instant = useMemo(() => {
    if (value === null || value === undefined || value.length === 0) return null;

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }, [value]);

  useEffect(() => {
    if (instant === null) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      timer = setInterval(() => setNow(Date.now()), RELATIVE_TICK_MS);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }

      // Read the clock BEFORE restarting: the operator is looking at the panel
      // again now, and waiting a full tick to correct an age that stood still
      // for an hour is the same staleness this hook exists to prevent.
      setNow(Date.now());
      start();
    };

    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [instant]);

  return useMemo(
    () =>
      instant === null
        ? { iso: null, relative: NO_INSTANT, absolute: NO_INSTANT }
        : {
            iso: value ?? null,
            relative: relativeOf(instant, now, locale),
            absolute: formatDateTime(value, locale, formats),
          },
    [instant, now, locale, formats, value],
  );
}
